import { useEffect, useState } from 'react';
import { fetchStudyStreak } from '../../services/studySession';
import { StudySessionWithExam } from '../../types/Study';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import Skeleton from '@mui/material/Skeleton';
import { Clock } from 'react-feather';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import Widget from '../../widgets';
import { WIDGET_KEYS } from '../../types/Widgets';

interface SessionStatus {
  isStarted: boolean;
  isComplete: boolean;
  isSnoozed: boolean;
  examName: string;
}

interface HeatmapDay {
  date: string;
  value: 0 | 1 | 2;
  isSnoozed: boolean;
  sessions: {
    examName: string;
    status: 'Complete' | 'Partially Complete' | 'Snoozed' | 'Not Started';
  }[];
}

interface HeatmapProps {
  isDemo?: boolean;
  showDelete?: boolean;
}

const Heatmap: React.FC<HeatmapProps> = ({
  isDemo = false,
  showDelete = false,
}) => {
  const todaySession = useSelector((state: RootState) => state.todaySession);

  const WEEKS = 9;
  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const [heatmapData, setHeatmapData] = useState<HeatmapDay[]>([]);
  const [showMonthDisplay] = useState<boolean>(true);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);

  useEffect(() => {
    if (isDemo) {
      generateDemoData();
    } else {
      loadSessions();
    }
  }, [todaySession, isDemo]);

  const generateDemoData = () => {
    const currentDate = moment();
    const startDate = moment().year(currentDate.year()).month(3).date(21); // April 21st of current year

    // Create a consistent pattern of activity
    const demoHeatmap: HeatmapDay[] = Array.from(
      { length: 7 * WEEKS },
      (_, i) => {
        const currentDate = moment(startDate);
        currentDate.add(i, 'days');
        const currentDateString = currentDate.format('YYYY-MM-DD');

        // Create a pattern where:
        // - Weekdays (Mon-Fri) have 70% chance of activity
        // - Weekends have 30% chance of activity
        // - Activity is either complete (value=2) or partial (value=1)
        const isWeekend = currentDate.day() === 0 || currentDate.day() === 6;
        const hasActivity = Math.random() < (isWeekend ? 0.3 : 0.7);
        const isComplete = Math.random() < 0.6; // 60% chance of being complete
        const isSnoozed = Math.random() < 0.1; // 10% chance of being snoozed

        let value: 0 | 1 | 2 = 0;
        if (hasActivity) {
          value = isComplete || isSnoozed ? 2 : 1;
        }

        return {
          date: currentDateString,
          value,
          isSnoozed,
          sessions: hasActivity
            ? [
                {
                  examName: 'Demo Exam',
                  status: !hasActivity
                    ? 'Not Started'
                    : isComplete
                      ? 'Complete'
                      : isSnoozed
                        ? 'Snoozed'
                        : 'Partially Complete',
                },
              ]
            : [],
        };
      }
    );

    // Calculate streaks based on the generated data
    let currentStreak = 0;
    let longestStreak = 0;
    let streak = 0;
    const todayString = moment().format('YYYY-MM-DD');

    for (let i = 0; i < demoHeatmap.length; i++) {
      const day = demoHeatmap[i];
      const hasActivity = day.value > 0;

      if (hasActivity) {
        streak++;
        if (day.date === todayString) {
          currentStreak = streak;
        }
      } else {
        streak = 0;
      }

      if (streak > longestStreak) {
        longestStreak = streak;
      }
    }

    setHeatmapData(demoHeatmap);
    setCurrentStreak(currentStreak);
    setLongestStreak(longestStreak);
  };

  const loadSessions = async () => {
    const heatmapData = await fetchStudyStreak();
    if (!heatmapData) return;
    const sessions = [...heatmapData.sessions];

    // Create a map of session data for quick lookup
    const sessionMap = sessions.reduce(
      (
        map: Record<
          string,
          {
            sessions: SessionStatus[];
          }
        >,
        session: StudySessionWithExam
      ) => {
        const dateString = session.date.split('T')[0];
        if (!map[dateString]) {
          map[dateString] = { sessions: [] };
        }

        map[dateString].sessions.push({
          examName: session?.exam?.name || 'Unknown Exam',
          isStarted: session.isStarted,
          isComplete: session.isComplete,
          isSnoozed: session.isSnoozed,
        });
        return map;
      },
      {}
    );

    calculateStreaks(sessionMap);

    const today = moment();
    const startDate = today
      .clone()
      .subtract(WEEKS - 1, 'weeks')
      .startOf('week')
      .add(1, 'days');

    const heatmap: HeatmapDay[] = Array.from({ length: 7 * WEEKS }, (_, i) => {
      const currentDate = moment(startDate);
      currentDate.add(i, 'days');
      const currentDateString = currentDate.format('YYYY-MM-DD');

      const daySessions = sessionMap[currentDateString]?.sessions || [];

      let value: 0 | 1 | 2 = 0;
      let isSnoozed = false;

      if (daySessions.length > 0) {
        // Check if any session is complete
        const hasComplete = daySessions.some(
          (s: SessionStatus) => s.isComplete
        );
        // Check if any session is started but not complete
        const hasStarted = daySessions.some(
          (s: SessionStatus) => s.isStarted && !s.isComplete
        );
        isSnoozed = daySessions.some((s: SessionStatus) => s.isSnoozed);

        if (isSnoozed || hasComplete) {
          value = 2;
        } else if (hasStarted) {
          value = 1;
        }

        return {
          date: currentDateString,
          value,
          isSnoozed,
          sessions: daySessions.map((session: SessionStatus) => ({
            examName: session.examName,
            status: !session.isStarted
              ? 'Not Started'
              : session.isComplete
                ? 'Complete'
                : session.isSnoozed
                  ? 'Snoozed'
                  : 'Partially Complete',
          })),
        };
      }

      return {
        date: currentDateString,
        value,
        isSnoozed,
        sessions: [],
      };
    });

    setHeatmapData(heatmap);
  };

  interface DaySessions {
    sessions: SessionStatus[];
  }

  interface SessionMap {
    [date: string]: DaySessions;
  }

  const calculateStreaks = (sessions: SessionMap) => {
    const dates = Object.keys(sessions).sort();
    let currentStreak = 0;
    let longestStreak = 0;
    let streak = 0;

    const today = moment().format('YYYY-MM-DD');
    let previousDate = moment(dates[0]);

    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      const daySessions = sessions[date];
      const currentDate = moment(date);

      if (i > 0 && !currentDate.isSame(previousDate.add(1, 'days'), 'day')) {
        streak = 0; // Reset streak if dates are not consecutive
      }

      // Any activity (complete, partially complete, or snoozed) counts for the streak
      const hasActivity = daySessions.sessions.some(
        (session: SessionStatus) =>
          session.isComplete || session.isStarted || session.isSnoozed
      );

      if (hasActivity) {
        streak++;
        if (date === today) {
          currentStreak = streak;
        }
      } else {
        streak = 0;
      }

      if (streak > longestStreak) {
        longestStreak = streak;
      }

      previousDate = currentDate.clone();
    }

    // If today is not present or has no activity but there is a current streak from yesterday
    if (
      !sessions[today] ||
      !sessions[today].sessions.some(
        (session: SessionStatus) =>
          session.isComplete || session.isStarted || session.isSnoozed
      )
    ) {
      const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');

      if (
        sessions[yesterday] &&
        sessions[yesterday].sessions.some(
          (session: SessionStatus) =>
            session.isComplete || session.isStarted || session.isSnoozed
        )
      ) {
        currentStreak++;
      }
    }

    setCurrentStreak(currentStreak);
    setLongestStreak(longestStreak);
  };

  const renderTooltipContent = (day: HeatmapDay) => {
    const dateStr = moment(day.date).format('MMMM D, YYYY');
    const sessionsList = day.sessions
      .filter((session) => session.status !== 'Not Started')
      .map(
        (session) =>
          `<div style="margin-bottom: .125rem;"><strong style="color: var(--type-heading-dark);">${session.examName}</strong>: ${session.status}</div>`
      )
      .join('');

    return `<strong style="display: block; color: var(--type-heading-dark); font-size: .75rem;">${dateStr}</strong><div style="${sessionsList.length > 0 ? 'margin-top: .25rem' : ''}">${sessionsList}</div>`;
  };

  const renderWeekDisplay = () => {
    if (heatmapData.length === 0) return null;
    return (
      <div className="grid-week">
        {heatmapData.slice(-7).map((day: HeatmapDay, i: number) => (
          <Tooltip
            enterTouchDelay={0}
            title={
              <div
                style={{ lineHeight: '1.3' }}
                dangerouslySetInnerHTML={{ __html: renderTooltipContent(day) }}
              />
            }
            key={i}
          >
            <div className="day">
              <div className={`cell val-${day.value}`}>
                {day.isSnoozed && <Clock />}
              </div>
              <div>{DAY_LABELS[i]}</div>
            </div>
          </Tooltip>
        ))}
      </div>
    );
  };

  const areAllValuesDefined = (
    ...values: (number | null | undefined | HeatmapDay[])[]
  ) => values.every((value) => value !== undefined && value !== null);

  const renderMonthDisplay = () => {
    if (heatmapData.length === 0) return null;
    const weekChunks = Array.from({ length: WEEKS }, (_, i) =>
      heatmapData.slice(i * 7, i * 7 + 7)
    );
    return (
      <div className="grid-month">
        <div className="labels">
          {DAY_LABELS.map((label, i) => (
            <div key={i} className="day">
              {label}
            </div>
          ))}
        </div>
        {weekChunks.map((week, weekIndex) => (
          <div key={weekIndex} className="week">
            {week.map((day, dayIndex) => {
              let future = false;
              if (moment(day.date).isAfter(moment(), 'day')) {
                future = true;
              }
              return (
                <Tooltip
                  enterTouchDelay={0}
                  title={
                    <div
                      style={{ lineHeight: '1.3' }}
                      dangerouslySetInnerHTML={{
                        __html: renderTooltipContent(day),
                      }}
                    />
                  }
                  key={dayIndex}
                >
                  <div
                    className={`day val-${day.value}${future ? ' future' : ''}`}
                  >
                    {day.isSnoozed && <Clock />}
                  </div>
                </Tooltip>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Widget
      widgetKey={WIDGET_KEYS.HEATMAP}
      name={'heatmap'}
      title="Study Streak"
      subtitle="Your study streak across all exams"
      showDelete={showDelete}
    >
      {areAllValuesDefined(currentStreak, longestStreak, heatmapData) && (
        <div className="heatmap">
          {showMonthDisplay ? renderMonthDisplay() : renderWeekDisplay()}
          <div className="heatmap_streaks">
            <div className="streak">
              <div className="streak_value">{currentStreak}</div>
              <div className="streak_label">Current Streak</div>
            </div>
            <div className="streak">
              <div className="streak_value">{longestStreak}</div>
              <div className="streak_label">Longest Streak</div>
            </div>
          </div>
        </div>
      )}
      {!areAllValuesDefined(currentStreak, longestStreak, heatmapData) && (
        <Skeleton
          sx={{
            bgcolor: 'rgba(255,255,255,.05)',
            borderRadius: '1rem',
          }}
          variant="rectangular"
          animation="wave"
          width={'100%'}
          height={105}
        />
      )}
    </Widget>
  );
};

export default Heatmap;
